import { useState, useEffect, useRef } from "react";
import { StyleSheet, View, Animated } from "react-native";

// import "../Localization/i18next";
import Colors from "../Resources/Color";

import { observer, Observer } from "mobx-react";
import MainView from "./CoachViews/MainView";
import CustomActivityIndicator from "./Components/CustomActivityIndicator";
import NetworkManager from "./Classes/NetworkManager";
import { useTranslation } from "react-i18next";
import { AuthGuard } from "./auth/guards/RouteGuards";
import { useAuth } from "./auth/AuthContext";
import { useCoachStore } from "./contexts/CoachStoreContext";
import { useToast } from "./contexts/ToastContext";

const Page = observer(() => {
  const coach = useCoachStore();
  const {
    user,
    justCompletedOnboarding,
    clearOnboardingFlag,
    needsOnboarding,
    loading,
  } = useAuth();
  const [showContent, setShowContent] = useState(false);
  const { t } = useTranslation();
  const { showToast } = useToast();
  const networkManager = NetworkManager.getInstance();
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const hasFetchedForUser = useRef(null); // Track which user we've fetched for

  useEffect(() => {
    if (typeof document === "undefined") return;

    document.title = "Efort Coach";

    const upsertMetaByName = (name, content) => {
      const existing = document.querySelector(`meta[name="${name}"]`);
      const meta = existing || document.createElement("meta");
      meta.setAttribute("name", name);
      meta.setAttribute("content", content);
      if (!existing) document.head.appendChild(meta);
    };

    const upsertCanonical = (href) => {
      const existing = document.querySelector('link[rel="canonical"]');
      const link = existing || document.createElement("link");
      link.setAttribute("rel", "canonical");
      link.setAttribute("href", href);
      if (!existing) document.head.appendChild(link);
    };

    upsertMetaByName(
      "description",
      "Efort Coach web app for coaches to plan training blocks, manage athletes, review videos and track billing."
    );
    upsertCanonical("https://app.efortcoach.com/");
  }, []);

  // Trigger fade-in animation when loading completes
  useEffect(() => {
    // Don't show content if auth is still loading, or if coach is loading/initializing
    const isLoading = loading || coach?.initialLoading;

    if (!isLoading && user && !needsOnboarding) {
      // Delay before starting fade-in
      const timer = setTimeout(
        () => {
          setShowContent(true);
          Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 100,
            useNativeDriver: true,
          }).start();
        },
        justCompletedOnboarding ? 2000 : 1100
      );

      return () => clearTimeout(timer);
    } else {
      // Reset when loading starts or conditions aren't met
      setShowContent(false);
      fadeAnim.setValue(0);
    }
  }, [
    loading,
    coach?.initialLoading,
    user,
    needsOnboarding,
    justCompletedOnboarding,
  ]);

  // Fetch coach data when user is available or when returning from onboarding
  useEffect(() => {
    // IMPORTANT: Wait for AuthContext to finish loading before doing anything
    if (loading) {
      console.log("[Index] AuthContext still loading, waiting...");
      return;
    }

    if (!user) {
      console.log("[Index] No user logged in, skipping coach data fetch");
      hasFetchedForUser.current = null;
      return;
    }

    // IMPORTANT: Don't fetch if user needs onboarding (will be redirected away)
    if (needsOnboarding) {
      console.log("[Index] User needs onboarding, skipping coach data fetch");
      return;
    }

    const fetchCoachData = async () => {
      // Check if we've already fetched for this user
      // We fetch if: 1) never fetched for this user, OR 2) just completed onboarding
      const shouldFetch =
        hasFetchedForUser.current !== user.uid || justCompletedOnboarding;

      if (!shouldFetch) {
        console.log("[Index] Already fetched for user:", user.uid);
        return;
      }

      console.log(
        "[Index] Fetching coach data for user:",
        user.uid,
        "justCompletedOnboarding:",
        justCompletedOnboarding
      );

      // Mark that we're fetching for this user BEFORE starting
      // But only if not coming from onboarding (to allow the fetch to run)
      if (!justCompletedOnboarding) {
        hasFetchedForUser.current = user.uid;
      }

      coach.setProperty("isFetching", true);
      coach.setProperty("initialLoading", true);
      coach.setProperty("showingCoachView", "CoachDashboard");

      await coach.fetchInit(
        user.uid,
        user.displayName,
        user.email,
        t,
        justCompletedOnboarding,
        showToast
      );

      coach.setProperty("isFetching", false);
      coach.setProperty("initialLoading", false);
      console.log("[Index] Coach data loaded");

      // Mark as fetched and clear flag AFTER fetch completes
      hasFetchedForUser.current = user.uid;
      if (justCompletedOnboarding) {
        clearOnboardingFlag();
      }
    };

    fetchCoachData();

    return () => {
      // Only cleanup listeners if we're unmounting and user is changing
      // (not when just clearing the onboarding flag)
      if (hasFetchedForUser.current !== user.uid) {
        coach.firestoreListener.removeClientsListener();
        coach.firestoreListener.removeInvitationsListener();
        coach.firestoreListener.removeChatsListener();
      }
    };
  }, [user?.uid, justCompletedOnboarding, needsOnboarding, loading]);

  useEffect(() => {
    networkManager.setupConnectionListener(coach, t);

    return () => {
      networkManager.removeConnectionListener();
    };
  }, []);

  useEffect(() => {
    networkManager.setupBeforeUnloadListener(coach);
    return () => {
      networkManager.removeBeforeUnloadListener();
    };
  }, [coach.isOffline]);

  return (
    <AuthGuard>
      <Observer>
        {() => (
          <>
            {!showContent || coach?.initialLoading ? (
              <View
                style={[
                  styles.container,
                  { backgroundColor: Colors.whiteColor },
                ]}
                showsVerticalScrollIndicator={false}
              >
                <CustomActivityIndicator />
              </View>
            ) : (
              <Animated.View
                style={[styles.container, { opacity: fadeAnim }]}
                showsVerticalScrollIndicator={false}
              >
                <MainView coach={coach} />
              </Animated.View>
            )}
          </>
        )}
      </Observer>
    </AuthGuard>
  );
});
export default Page;

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    backgroundColor: Colors.backgroundColor,
    flex: 1,
    width: "100%",
    height: "100%",
  },
});
